import React, { useEffect } from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Modal,
  Form,
  Input,
  Select,
  Tabs,
  InputNumber,
  Switch,
  Space,
  Typography,
  Tooltip,
  Divider,
} from 'antd';

import { AdProject, ProjectFormValues, PAGE_GOALS, AdAccount } from '../types';

const { TextArea } = Input;
const { Text } = Typography;

interface ProjectFormModalProps {
  visible: boolean;
  project: AdProject | null;
  adAccounts: AdAccount[];
  onCancel: () => void;
  onSubmit: (values: ProjectFormValues) => void;
  loading?: boolean;
}

const ProjectFormModal: React.FC<ProjectFormModalProps> = ({
  visible,
  project,
  adAccounts,
  onCancel,
  onSubmit,
  loading = false,
}) => {
  const [form] = Form.useForm<ProjectFormValues>();
  const isEditing = !!project;

  useEffect(() => {
    if (visible) {
      if (project) {
        form.setFieldsValue({
          name: project.name,
          landingUrl: project.landingUrl,
          pageGoal: project.pageGoal,
          productName: project.paidProduct.name,
          productKeyIdea: project.paidProduct.keyIdea,
          productAverageCheck: project.paidProduct.averageCheck,
          productSalesPageUrl: project.paidProduct.salesPageUrl,
          companyName: project.companyInfo.companyName,
          companyActivity: project.companyInfo.companyActivity,
          authorName: project.companyInfo.authorName,
          authorDescription: project.companyInfo.authorDescription,
          adAccountIds: project.adAccountIds,
          excludeWords: project.settings.excludeWords.join(', '),
          keywords: project.settings.keywords.join(', '),
          notes: project.notes,
          mode: project.mode,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({ mode: 'manual' });
      }
    }
  }, [visible, project, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const tabItems = [
    {
      key: 'basic',
      label: 'Основное',
      forceRender: true,
      children: (
        <>
          <Form.Item
            name="name"
            label="Название рекламного проекта"
            rules={[{ required: true, message: 'Введите название проекта' }]}
          >
            <Input placeholder="Например: Курс по маркетингу" />
          </Form.Item>

          <Form.Item
            name="landingUrl"
            label="URL адрес рекламируемой страницы"
            rules={[
              { required: true, message: 'Введите URL страницы' },
              { type: 'url', message: 'Введите корректный URL' },
            ]}
          >
            <Input placeholder="https://example.com/landing" />
          </Form.Item>

          <Form.Item
            name="pageGoal"
            label="Задача рекламируемой страницы"
            rules={[{ required: true, message: 'Выберите задачу страницы' }]}
          >
            <Select
              placeholder="Выберите задачу"
              options={PAGE_GOALS}
            />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'product',
      label: 'Платный продукт',
      forceRender: true,
      children: (
        <>
          <Form.Item
            name="productName"
            label="Название продаваемого продукта"
            rules={[{ required: true, message: 'Введите название продукта' }]}
          >
            <Input placeholder="Например: Курс «Основы маркетинга»" />
          </Form.Item>

          <Form.Item
            name="productKeyIdea"
            label={
              <Space>
                Ключевая мысль
                <Tooltip title="Описываем уникальность продукта. За счёт чего достигается результат?">
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Опишите ключевую мысль' }]}
          >
            <TextArea
              rows={3}
              placeholder="Опишите уникальность продукта и за счёт чего достигается результат"
            />
          </Form.Item>

          <Form.Item
            name="productAverageCheck"
            label="Средний чек продукта"
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0"
              min={0}
              addonAfter="₽"
            />
          </Form.Item>

          <Form.Item
            name="productSalesPageUrl"
            label="URL адрес продающего сайта"
            rules={[{ type: 'url', message: 'Введите корректный URL' }]}
          >
            <Input placeholder="https://example.com/sales" />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'company',
      label: 'О компании',
      forceRender: true,
      children: (
        <>
          <Form.Item
            name="companyName"
            label="Название компании"
          >
            <Input placeholder="ООО «Компания»" />
          </Form.Item>

          <Form.Item
            name="companyActivity"
            label="Чем занимается компания"
          >
            <TextArea rows={2} placeholder="Краткое описание деятельности компании" />
          </Form.Item>

          <Divider orientation="left">Автор / Преподаватель</Divider>

          <Form.Item
            name="authorName"
            label="Имя Фамилия автора"
          >
            <Input placeholder="Иван Иванов" />
          </Form.Item>

          <Form.Item
            name="authorDescription"
            label="Краткое описание автора и его регалии"
          >
            <TextArea
              rows={3}
              placeholder="Эксперт с 10-летним опытом, автор 5 книг..."
            />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'adAccounts',
      label: 'Рекламные кабинеты',
      forceRender: true,
      children: (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Выберите рекламные кабинеты, в которые будут отправляться объявления
          </Text>

          <Form.Item
            name="adAccountIds"
            label="Подключённые кабинеты"
          >
            <Select
              mode="multiple"
              placeholder="Выберите кабинеты"
              options={adAccounts.map(acc => ({
                value: acc.id,
                label: `${acc.name} (${acc.type === 'yandex_direct' ? 'Яндекс Директ' : 'ВКонтакте'})`,
                disabled: !acc.isConnected,
              }))}
            />
          </Form.Item>

          <Text type="secondary">
            Нет нужного кабинета?{' '}
            <a href="/source/ads" target="_blank">Подключить новый</a>
          </Text>
        </>
      ),
    },
    {
      key: 'settings',
      label: 'Настройки',
      forceRender: true,
      children: (
        <>
          <Form.Item
            name="excludeWords"
            label={
              <Space>
                Слова исключения
                <Tooltip title="Слова, которые не нужно использовать в рекламе. Введите через запятую">
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            }
          >
            <TextArea
              rows={2}
              placeholder="бесплатно, халява, скидка..."
            />
          </Form.Item>

          <Form.Item
            name="keywords"
            label={
              <Space>
                Ключевые слова проекта
                <Tooltip title="Слова, которые характеризуют проект. Введите через запятую">
                  <QuestionCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </Space>
            }
          >
            <TextArea
              rows={2}
              placeholder="маркетинг, продвижение, реклама..."
            />
          </Form.Item>

          <Divider />

          <Form.Item
            name="mode"
            label="Режим работы"
            valuePropName="checked"
            getValueFromEvent={(checked) => checked ? 'auto' : 'manual'}
            getValueProps={(value) => ({ checked: value === 'auto' })}
          >
            <Switch
              checkedChildren="Автоматический"
              unCheckedChildren="Ручной"
            />
          </Form.Item>
          <Text type="secondary">
            В ручном режиме вы сможете редактировать объявления перед отправкой.
            В автоматическом — объявления отправляются сразу после генерации.
          </Text>
        </>
      ),
    },
    {
      key: 'notes',
      label: 'Заметки',
      forceRender: true,
      children: (
        <Form.Item
          name="notes"
          label="Заметки по проекту"
        >
          <TextArea
            rows={6}
            placeholder="Любые заметки и комментарии по проекту..."
          />
        </Form.Item>
      ),
    },
  ];

  return (
    <Modal
      title={isEditing ? 'Редактирование проекта' : 'Создание рекламного проекта'}
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      width={700}
      okText={isEditing ? 'Сохранить' : 'Создать'}
      cancelText="Отмена"
      confirmLoading={loading}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ mode: 'manual' }}
      >
        <Tabs items={tabItems} />
      </Form>
    </Modal>
  );
};

export default ProjectFormModal;
